import { useCallback, useEffect, useRef, useState } from 'react';
import {
  VALL_DROP_BOX_TIME_LIMIT_SEC,
  VALL_VICTORY_HOLD_MS,
} from '../../../../domain';
import {
  VALL_BUTTON_URL,
  VALL_HEADER_URL,
  VALL_MIDDLE_URL,
  VALL_PACKAGE_URLS,
  VALL_PACKAGE1_URL,
  VALL_TILE_URL,
  VALL_TIMER_URL,
  VALL_VICTORY_URL,
} from '../assets';
import './VallDropBoxGame.css';

export type VallDropBoxGameProps = {
  onWin: () => void;
  onLose: () => void;
  onTimerChange?: (secondsLeft: number) => void;
};

type Phase = 'play' | 'victory';

const BOX_SIZE = 169;
const GAP = 280;
const ROW_SPEED_PX = 160;
const DROP_SPEED_PX = 900;
const HANG_TOP = 0;
const ROW_TOP = 521;
const BOX_COUNT = 12;

function formatVallTimer(secondsLeft: number): string {
  const safe = Math.max(0, secondsLeft);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} сек`;
}

export function VallDropBoxGame({
  onWin,
  onLose,
  onTimerChange,
}: VallDropBoxGameProps) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(VALL_DROP_BOX_TIME_LIMIT_SEC);
  const [phase, setPhase] = useState<Phase>('play');
  const [finished, setFinished] = useState(false);
  const [dropping, setDropping] = useState(false);
  const [dropY, setDropY] = useState(HANG_TOP);
  const [rowOffset, setRowOffset] = useState(0);
  const rowOffsetRef = useRef(0);
  const droppingRef = useRef(false);
  const finishedRef = useRef(false);

  useEffect(() => {
    onTimerChange?.(secondsLeft);
  }, [onTimerChange, secondsLeft]);

  useEffect(() => {
    finishedRef.current = finished;
  }, [finished]);

  useEffect(() => {
    if (phase !== 'play' || finished) {
      return;
    }
    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [finished, phase]);

  useEffect(() => {
    if (finished || phase !== 'play' || secondsLeft > 0) {
      return;
    }
    setFinished(true);
    onLose();
  }, [finished, onLose, phase, secondsLeft]);

  useEffect(() => {
    if (phase !== 'victory') {
      return;
    }
    const timer = window.setTimeout(() => {
      onWin();
    }, VALL_VICTORY_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [onWin, phase]);

  useEffect(() => {
    if (finished || phase !== 'play') {
      return;
    }
    let raf = 0;
    let last = performance.now();
    const cycle = BOX_SIZE + GAP;
    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!droppingRef.current) {
        rowOffsetRef.current = (rowOffsetRef.current + ROW_SPEED_PX * dt) % cycle;
        setRowOffset(rowOffsetRef.current);
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [finished, phase]);

  const isOverGap = useCallback((stageWidth: number, offset: number) => {
    const centerX = stageWidth / 2;
    const unit = BOX_SIZE + GAP;
    // Pattern: [box][gap][box][gap]... — win only if the full drop box fits in the gap.
    const local = ((centerX + offset) % unit + unit) % unit;
    const half = BOX_SIZE / 2;
    return local >= BOX_SIZE + half && local <= BOX_SIZE + GAP - half;
  }, []);

  const miss = useCallback(() => {
    setDropping(false);
    droppingRef.current = false;
    setDropY(HANG_TOP);
  }, []);

  const drop = useCallback(() => {
    if (finishedRef.current || droppingRef.current || phase !== 'play') {
      return;
    }
    const stage = stageRef.current;
    if (stage == null) {
      return;
    }
    droppingRef.current = true;
    setDropping(true);
    const targetY = ROW_TOP;
    const start = performance.now();
    const startY = HANG_TOP;

    const animate = (now: number) => {
      const elapsed = (now - start) / 1000;
      const y = Math.min(targetY, startY + DROP_SPEED_PX * elapsed);
      setDropY(y);
      if (y < targetY) {
        window.requestAnimationFrame(animate);
        return;
      }
      const hit = isOverGap(stage.clientWidth, rowOffsetRef.current);
      if (hit) {
        finishedRef.current = true;
        setFinished(true);
        setPhase('victory');
      } else {
        miss();
      }
    };
    window.requestAnimationFrame(animate);
  }, [isOverGap, miss, phase]);

  const boxes = Array.from({ length: BOX_COUNT }, (_, i) => i);
  const controlsDisabled = finished || dropping || phase !== 'play';

  return (
    <div className="vall-drop">
      <img className="vall-drop__tile" src={VALL_TILE_URL} alt="" draggable={false} />
      <img className="vall-drop__header" src={VALL_HEADER_URL} alt="" draggable={false} />
      <img className="vall-drop__middle" src={VALL_MIDDLE_URL} alt="" draggable={false} />
      <h2 className="vall-drop__title">Положи посылку</h2>
      <p className="vall-drop__hint">Не задень другие посылки</p>

      <div
        ref={stageRef}
        className="vall-drop__stage"
        onClick={drop}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            drop();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Сбросить коробку"
      >
        <img
          className="vall-drop__drop"
          src={VALL_PACKAGE1_URL}
          alt=""
          draggable={false}
          style={{ top: dropY }}
        />
        <div
          className="vall-drop__row"
          style={{ transform: `translateX(${-rowOffset}px)` }}
        >
          {boxes.map((i) => (
            <img
              key={i}
              className="vall-drop__box"
              src={VALL_PACKAGE_URLS[i % VALL_PACKAGE_URLS.length]!}
              alt=""
              draggable={false}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        className="vall-drop__drop-btn"
        onClick={drop}
        disabled={controlsDisabled}
        aria-label="Сбросить"
      >
        <img
          className="vall-drop__drop-btn-art"
          src={VALL_BUTTON_URL}
          alt=""
          draggable={false}
        />
      </button>

      <div className="vall-drop__timer">
        <img className="vall-drop__timer-art" src={VALL_TIMER_URL} alt="" draggable={false} />
        <p className="vall-drop__timer-value">{formatVallTimer(secondsLeft)}</p>
      </div>

      {phase === 'victory' ? (
        <div className="vall-drop__victory" aria-live="polite">
          <img
            className="vall-drop__victory-art"
            src={VALL_VICTORY_URL}
            alt=""
            draggable={false}
          />
        </div>
      ) : null}
    </div>
  );
}
